import React, { useState } from 'react'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle'
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut'
import CopyIcon from '@atlaskit/icon/glyph/copy'

/**
 * SUPPORT MODAL
 *
 * Lightweight Help & Support dialog opened from the "Support" item in the
 * global sidebar. It is intentionally simple (no backend): it points users to
 * the documentation site and gives them a way to email the support team.
 *
 * Two actions:
 *  - "Open documentation" → opens the docs site in a new browser tab.
 *  - "Email support"      → opens the user's mail client with a pre-filled
 *                           message. A "Copy" button is provided as a reliable
 *                           fallback in case the mailto link is blocked inside
 *                           the Jira Connect iframe.
 */

// Support contact details — update here if the docs URL or email ever change.
const DOCS_URL = 'https://docs.zencod.net/'
const SUPPORT_EMAIL = 'support@zencod.net'

function SupportModal({ isOpen, onClose }) {
  // Tracks the brief "Copied!" confirmation shown on the copy button
  const [copied, setCopied] = useState(false)

  /**
   * Copy the support email address to the clipboard.
   * Uses the async Clipboard API with a legacy execCommand fallback for
   * environments where the Clipboard API is unavailable or blocked.
   */
  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL)
      setCopied(true)
    } catch (err) {
      // Fallback: temporary textarea + execCommand('copy')
      try {
        const textarea = document.createElement('textarea')
        textarea.value = SUPPORT_EMAIL
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        setCopied(true)
      } catch (fallbackErr) {
        console.error('Failed to copy support email:', fallbackErr)
      }
    }
    // Revert the label back to "Copy" after 2 seconds
    setTimeout(() => setCopied(false), 2000)
  }

  /**
   * Open the documentation site in a new browser tab.
   * window.open with 'noopener,noreferrer' matches the pattern used elsewhere
   * (see jiraContext.js) and works reliably from inside the Connect iframe.
   */
  const handleOpenDocs = () => {
    window.open(DOCS_URL, '_blank', 'noopener,noreferrer')
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - pointer-events:none prevents click detection, Panel has pointer-events:auto */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '440px',
            maxHeight: '90vh',
            overflow: 'auto',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-information, #DEEBFF)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <QuestionCircleIcon
                label="Support"
                size="medium"
                primaryColor="var(--ds-icon-information, #0052CC)"
              />
            </div>
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              Help &amp; Support
            </Dialog.Title>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 24px' }}>
            <p style={{
              fontSize: '14px',
              color: 'var(--ds-text-subtle, #44546F)',
              margin: '0 0 20px 0'
            }}>
              Find answers in our docs, or get in touch with our team.
            </p>

            {/* Documentation section */}
            <div>
              <h3 style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--ds-text, #172B4D)',
                margin: '0 0 4px 0'
              }}>
                Documentation
              </h3>
              <p style={{
                fontSize: '13px',
                color: 'var(--ds-text-subtlest, #6B778C)',
                margin: '0 0 10px 0'
              }}>
                Browse setup guides, FAQs, and tutorials for Test Management.
              </p>
              <Button
                appearance="default"
                onClick={handleOpenDocs}
                iconAfter={<ShortcutIcon label="" size="small" />}
              >
                Open documentation
              </Button>
            </div>

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: 'var(--ds-border, #DFE1E6)',
              margin: '20px 0'
            }} />

            {/* Contact section */}
            <div>
              <h3 style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--ds-text, #172B4D)',
                margin: '0 0 4px 0'
              }}>
                Still need help?
              </h3>
              <p style={{
                fontSize: '13px',
                color: 'var(--ds-text-subtlest, #6B778C)',
                margin: '0 0 10px 0'
              }}>
                Email our team with your question or issue — feel free to attach
                screenshots or files.
              </p>

              {/* Email row — selectable address + copy button */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  flex: 1,
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--ds-text, #172B4D)',
                  userSelect: 'all',
                  padding: '6px 10px',
                  backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                  borderRadius: '4px'
                }}>
                  {SUPPORT_EMAIL}
                </span>
                <Button
                  appearance="subtle"
                  onClick={handleCopyEmail}
                  iconBefore={<CopyIcon label="" size="small" />}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Button appearance="subtle" onClick={onClose}>
              Close
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { SupportModal }
